import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Html5Qrcode } from "html5-qrcode";
import {
    Camera, X, Check, Crown, Ban, ShieldAlert, ScanLine, LogOut, Loader2, Users, Search, Instagram, Phone, LogIn,
} from "lucide-react";
import { toast } from "sonner";
import { useT } from "../lib/i18n";
import { api, photoUrl } from "../lib/api";
import LanguageToggle from "../components/LanguageToggle";
import useKeepAlive from "../lib/useKeepAlive";
import KeepAliveBadge from "../components/KeepAliveBadge";

const QR_REGION_ID = "staff-qr-region";

function ScannerPane() {
    const { t } = useT();
    const scannerRef = useRef(null);
    const [scanning, setScanning] = useState(false);
    const [member, setMember] = useState(null);
    const [busy, setBusy] = useState(false);
    const [manual, setManual] = useState("");

    const stopScanner = useCallback(async () => {
        try {
            if (scannerRef.current) {
                await scannerRef.current.stop();
                await scannerRef.current.clear();
            }
        } catch { /* */ }
        scannerRef.current = null;
        setScanning(false);
    }, []);

    const verify = useCallback(async (payload) => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/scan", { payload });
            setMember(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("scan.error"));
        } finally {
            setBusy(false);
        }
    }, [t]);

    const startScanner = useCallback(async () => {
        setMember(null);
        setScanning(true);
        try {
            const instance = new Html5Qrcode(QR_REGION_ID, /* verbose */ false);
            scannerRef.current = instance;
            await instance.start(
                { facingMode: "environment" },
                {
                    fps: 25,
                    qrbox: { width: 280, height: 280 },
                    aspectRatio: 1.0,
                    disableFlip: true,
                },
                async (decoded) => {
                    await stopScanner();
                    await verify(decoded);
                },
                () => { /* */ }
            );
        } catch (e) {
            toast.error(e?.message || "Camera access denied.");
            setScanning(false);
        }
    }, [stopScanner, verify]);

    useEffect(() => () => { stopScanner(); }, [stopScanner]);

    const onManual = async (e) => {
        e.preventDefault();
        if (!manual.trim()) return;
        await verify(manual.trim());
        setManual("");
    };

    const checkIn = async () => {
        if (!member) return;
        try {
            const { data } = await api.post(`/admin/applications/${member.id}/check-in`);
            if (data?.duplicate) {
                toast(t("occ.duplicate"));
            } else if (data?.already_inside) {
                toast(t("occ.already_inside"));
            } else {
                toast.success(`${t("scan.checked_in")}${data?.by ? " · " + data.by : ""}`);
            }
            // refresh
            const r = await api.post("/admin/scan", { payload: member.id });
            setMember(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Check-in failed");
        }
    };

    const checkOut = async () => {
        if (!member) return;
        try {
            await api.post(`/admin/applications/${member.id}/check-out`);
            toast.success(t("occ.checked_out"));
            const r = await api.post("/admin/scan", { payload: member.id });
            setMember(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Check-out failed");
        }
    };

    const reset = async () => { setMember(null); await startScanner(); };

    const isBlacklisted = member?.status === "blacklist";
    const isActive = member && (member.status === "approved" || member.status === "vip");

    return (
        <div className="space-y-6">
            {!member && (
                <div className="rounded-sm border border-emerald-line/60 bg-bg-secondary/60 p-4">
                    <div id={QR_REGION_ID} className="w-full max-w-md mx-auto rounded-sm overflow-hidden bg-black" style={{ minHeight: scanning ? 260 : 0 }} />
                    <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
                        {!scanning ? (
                            <button onClick={startScanner} data-testid="start-scan"
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gold text-black rounded-sm font-medium hover:bg-gold-bright">
                                <Camera size={16} /> {t("scan.start")}
                            </button>
                        ) : (
                            <button onClick={stopScanner} data-testid="stop-scan"
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-gold/40 text-gold rounded-sm hover:bg-gold/10">
                                <X size={16} /> {t("scan.stop")}
                            </button>
                        )}
                    </div>

                    <form onSubmit={onManual} className="mt-8 border-t border-emerald-line/60 pt-6 flex flex-col sm:flex-row gap-3">
                        <input
                            value={manual}
                            onChange={(e) => setManual(e.target.value)}
                            placeholder={t("scan.manual")}
                            className="input-line flex-1"
                            data-testid="manual-input"
                        />
                        <button type="submit" disabled={busy || !manual.trim()} data-testid="manual-verify"
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-gold/40 text-gold rounded-sm hover:bg-gold/10 disabled:opacity-40">
                            {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                            {t("scan.verify")}
                        </button>
                    </form>
                </div>
            )}

            <AnimatePresence>
                {member && (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className={`relative rounded-sm border p-6 ${
                            isBlacklisted ? "border-red-700/60 bg-red-950/30" :
                            isActive ? "border-emerald-500/50 bg-emerald-950/20" :
                            "border-amber-500/50 bg-amber-950/20"
                        }`}
                        data-testid="scan-result"
                    >
                        <div className="flex items-start gap-4">
                            <div className="w-20 h-20 rounded-sm overflow-hidden bg-bg-surface border border-emerald-line/60 flex-shrink-0">
                                {member.photo_id && <img src={photoUrl(member.photo_id)} alt={member.full_name} className="w-full h-full object-cover" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <div className="font-serif text-2xl text-ink-primary truncate">{member.full_name}</div>
                                    {member.vip && <Crown size={18} className="text-gold flex-shrink-0" />}
                                </div>
                                <div className="text-sm text-ink-secondary mt-1">@{member.instagram} · {member.phone}</div>
                                <div className="text-xs text-ink-muted mt-1">Visits: {member.check_ins} · Status: {member.status?.toUpperCase()}</div>
                            </div>
                        </div>

                        <div className="mt-6">
                            {isBlacklisted ? (
                                <div className="flex items-center gap-3 text-red-300 font-medium"><Ban size={20} /> {t("scan.blacklist_warn")}</div>
                            ) : isActive ? (
                                <div className="flex items-center gap-3 text-emerald-300 font-medium"><Check size={20} /> {t("scan.ok_admit")}</div>
                            ) : (
                                <div className="flex items-center gap-3 text-amber-300 font-medium">
                                    <ShieldAlert size={20} /> {t("scan.inactive_warn")} ({member.status})
                                </div>
                            )}
                        </div>

                        <div className="mt-6 flex flex-col sm:flex-row gap-3">
                            {isActive && (
                                member.currently_inside ? (
                                    <button onClick={checkOut} data-testid="scan-check-out"
                                        className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 border border-emerald-400/60 text-emerald-200 rounded-sm hover:bg-emerald-400/10">
                                        <LogIn size={16} className="rotate-180" /> {t("occ.check_out")}
                                    </button>
                                ) : (
                                    <button onClick={checkIn} data-testid="scan-check-in"
                                        className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gold text-black rounded-sm font-medium hover:bg-gold-bright">
                                        <Check size={16} /> {t("scan.check_in_cta")}
                                    </button>
                                )
                            )}
                            <button onClick={reset} data-testid="scan-again"
                                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 border border-gold/40 text-gold rounded-sm hover:bg-gold/10">
                                <Camera size={16} /> {t("scan.scan_again")}
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function MembersPane() {
    const { t } = useT();
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/staff/members", { params: q.trim().length >= 2 ? { q: q.trim() } : {} });
            setItems(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, [q]);

    useEffect(() => {
        const id = setTimeout(load, 500);
        return () => clearTimeout(id);
    }, [load]);

    const checkIn = async (m) => {
        try {
            const { data } = await api.post(`/admin/applications/${m.id}/check-in`);
            if (data?.duplicate) toast(t("occ.duplicate"));
            else if (data?.already_inside) toast(t("occ.already_inside"));
            else toast.success(`${t("scan.checked_in")}${data?.by ? " · " + data.by : ""}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Check-in failed");
        }
    };

    return (
        <div className="space-y-4">
            <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={t("staff.members_search_ph")}
                    data-testid="staff-search"
                    className="w-full pl-9 pr-3 py-2.5 bg-bg-secondary/70 border border-emerald-line/60 rounded-sm text-sm focus:border-gold/60 outline-none"
                />
            </div>

            <div className="rounded-sm border border-emerald-line/60 overflow-hidden bg-bg-secondary/40">
                {loading && items.length === 0 ? (
                    <div className="p-8 text-center text-ink-muted text-sm">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="p-8 text-center text-ink-muted text-sm">{t("staff.no_members")}</div>
                ) : (
                    <ul className="divide-y divide-emerald-line/60" data-testid="staff-members-list">
                        {items.map((m) => (
                            <li key={m.id} className="flex items-center gap-4 px-4 py-3" data-testid={`staff-row-${m.id}`}>
                                <div className="w-11 h-11 rounded-full overflow-hidden bg-bg-surface border border-emerald-line/60 flex-shrink-0">
                                    {m.photo_id && <img src={photoUrl(m.photo_id)} alt={m.full_name} className="w-full h-full object-cover" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <div className="font-medium truncate">{m.full_name}</div>
                                        {m.vip && <Crown size={13} className="text-gold flex-shrink-0" />}
                                    </div>
                                    <div className="text-[11px] text-ink-secondary flex flex-wrap gap-x-3">
                                        <span className="inline-flex items-center gap-1"><Instagram size={10} />@{m.instagram}</span>
                                        <span className="inline-flex items-center gap-1"><Phone size={10} />{m.phone}</span>
                                        <span className="text-ink-muted">{m.check_ins} {t("staff.visits")}</span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => checkIn(m)}
                                    data-testid={`checkin-${m.id}`}
                                    className="px-3 py-1.5 bg-gold/15 text-gold border border-gold/40 rounded-sm hover:bg-gold/25 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-1.5"
                                >
                                    <Check size={12} /> {t("staff.check_in")}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export default function StaffDashboard() {
    const { t } = useT();
    const navigate = useNavigate();
    const [tab, setTab] = useState("scan");
    const [occupancy, setOccupancy] = useState(null);
    const staffName = localStorage.getItem("sc_staff_name") || "";
    const keepalive = useKeepAlive({ enabled: true });

    useEffect(() => {
        const t1 = localStorage.getItem("sc_staff_token");
        const t2 = localStorage.getItem("sc_admin_token");
        if (!t1 && !t2) navigate("/staff/login", { replace: true });
    }, [navigate]);

    // Live occupancy poll (every 10s while on dashboard)
    useEffect(() => {
        let mounted = true;
        const tick = async () => {
            try {
                const { data } = await api.get("/admin/occupancy");
                if (mounted) setOccupancy(data);
            } catch { /* */ }
        };
        tick();
        const id = setInterval(tick, 10000);
        return () => { mounted = false; clearInterval(id); };
    }, []);

    const signout = () => {
        localStorage.removeItem("sc_staff_token");
        localStorage.removeItem("sc_staff_name");
        navigate("/staff/login", { replace: true });
    };

    return (
        <div className="min-h-[100dvh] text-ink-primary" data-testid="staff-dashboard">
            <header className="sticky top-0 z-30 glass-strong border-b border-emerald-line/60">
                <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <ScanLine size={16} className="text-gold" />
                        <span className="font-serif text-lg tracking-wide">ScottsClub · Door</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <KeepAliveBadge online={keepalive.online} wakeLockActive={keepalive.wakeLockActive} />
                        <LanguageToggle />
                        {staffName && (
                            <div className="hidden sm:block text-xs text-ink-secondary">
                                {t("staff.welcome")} <span className="text-gold">{staffName}</span>
                            </div>
                        )}
                        <button onClick={signout} data-testid="staff-signout"
                            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink-secondary hover:text-gold">
                            <LogOut size={14} /> {t("staff.signout")}
                        </button>
                    </div>
                </div>
                <div className="gold-divider opacity-30" />
            </header>

            <main className="max-w-3xl mx-auto px-6 py-8">
                {/* Live occupancy bar */}
                <div className="grid grid-cols-2 gap-3 mb-6" data-testid="occupancy-strip">
                    <div className="rounded-sm border border-gold/30 bg-bg-secondary/70 p-4">
                        <div className="text-[10px] tracking-[0.28em] uppercase text-ink-muted">{t("occ.inside_now")}</div>
                        <div className="font-serif text-4xl gold-text mt-1" data-testid="inside-now">{occupancy?.inside_now ?? "—"}</div>
                    </div>
                    <div className="rounded-sm border border-emerald-line/60 bg-bg-secondary/70 p-4">
                        <div className="text-[10px] tracking-[0.28em] uppercase text-ink-muted">{t("occ.entries_tonight")}</div>
                        <div className="font-serif text-4xl text-ink-primary mt-1" data-testid="entries-tonight">{occupancy?.entries_tonight ?? "—"}</div>
                    </div>
                </div>

                <div className="flex gap-1 border border-emerald-line/60 rounded-sm p-1 bg-bg-secondary/40 mb-6 w-fit">
                    <button onClick={() => setTab("scan")} data-testid="tab-scan"
                        className={`px-4 py-2 text-[11px] tracking-[0.22em] uppercase rounded-sm transition-colors inline-flex items-center gap-2 ${tab === "scan" ? "bg-gold text-black" : "text-ink-secondary hover:text-gold"}`}>
                        <ScanLine size={13} /> {t("staff.tabs.scan")}
                    </button>
                    <button onClick={() => setTab("members")} data-testid="tab-members"
                        className={`px-4 py-2 text-[11px] tracking-[0.22em] uppercase rounded-sm transition-colors inline-flex items-center gap-2 ${tab === "members" ? "bg-gold text-black" : "text-ink-secondary hover:text-gold"}`}>
                        <Users size={13} /> {t("staff.tabs.members")}
                    </button>
                </div>

                {tab === "scan" ? <ScannerPane /> : <MembersPane />}
            </main>
        </div>
    );
}
